import React, { useState, useRef, useMemo} from 'react';
import CorrectedAgeCalculator from './Calculator';
import Appointments from './Appointments';
import GrowthCharts from './GrowthCharts';
import Monitoring from './Monitoring';
import {Card, CardBody} from "@heroui/card";
import AppsHeader from '../components/AppsHeader';
import { ArrowDown, ArrowLeft } from './ArrowIcons';
import './CorrectedAge.scss';

interface CorrectedAgeProps {
  hospital: string;
}

interface CalculatorRef {
  resetInputs: () => void;
}

const CorrectedAge: React.FC<CorrectedAgeProps> = () => {
  const calculatorRef = useRef<CalculatorRef>(null);
  const growthCardRef = useRef<HTMLDivElement>(null);
  const hearingCardRef = useRef<HTMLDivElement>(null);
  const [showGrowth, setShowGrowth] = useState(false);
  const [showHearing, setShowHearing] = useState(false);
  const [gestationWeeks, setGestationWeeks] = useState<string>('');
  const [isCalculated, setIsCalculated] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  const [fixedAgeInWeeks, setFixedAgeInWeeks] = useState<number | ''>('');
  const [recommendedCheckDate, setRecommendedCheckDate] = useState<string>('');
  const [recommendedSupportWorkshopDate, setRecommendedSupportWorkshopDate] = useState<string>('');
  const [gender, setGender] = useState<'boy' | 'girl' | undefined>('boy');
  const [fentonHC, setFentonHC] = useState<string>('');
  const [fentonLength, setFentonLength] = useState<string>('');
  const [fentonWeight, setFentonWeight] = useState<string>('');
  const [recommendedAbrStartDate, setrecommendedAbrStartDate] = useState<string>('');
  const [recommendedAbrEndDate, setrecommendedAbrEndDate] = useState<string>('');
  const [recommendedHearingTest1StartDate, setRecommendedHearingTest1StartDate] = useState<string>('');
  const [recommendedHearingTest1EndDate, setRecommendedHearingTest1EndDate] = useState<string>('');
  const [recommendedHearingTest2Date, setRecommendedHearingTest2Date] = useState<string>('');
  const [recommendedHearingTest3Date, setRecommendedHearingTest3Date] = useState<string>('');
  const [birthDate, setBirthDate] = useState<Date | null>(null);

  const isPremature = useMemo(() => parseInt(gestationWeeks, 10) < 37, [gestationWeeks]);

  const scrollToCard = (ref: React.RefObject<HTMLDivElement>) => {
    setTimeout(() => {
      ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleReset = () => {
    if (calculatorRef.current) {
      calculatorRef.current.resetInputs();
    }

    setIsCalculated(false);
    setIsDirty(false);
    setFixedAgeInWeeks('');
    setGestationWeeks('');
    setRecommendedCheckDate('');
    setRecommendedSupportWorkshopDate('');
    setGender('boy');
    setFentonHC('');
    setFentonLength('');
    setFentonWeight('');
    setrecommendedAbrStartDate('');
    setrecommendedAbrEndDate('');
    setRecommendedHearingTest1StartDate('');
    setRecommendedHearingTest1EndDate('');
    setRecommendedHearingTest2Date('');
    setRecommendedHearingTest3Date('');
    setBirthDate(null);
  };

  const handleCalculation = (calculated: boolean) => {
    setIsCalculated(calculated);
    setIsDirty(false);
  };

  const handleInputChange = (isDirty: boolean) => {
    setIsDirty(isDirty);
  };

  const getCurrentDate = () => {
    const params = new URLSearchParams(window.location.search);
    const testDate = params.get('__test_current_date');
    return testDate ? new Date(testDate) : new Date();
};

  return (
    <div className="container main-content corrected-age-container">
      <div style={{textAlign: 'right', overflow: 'auto'}}>
        <AppsHeader title="גיל מתוקן" />
        <div style={{marginBottom: '20px', padding: '0 2px' }}>
              <CorrectedAgeCalculator
                ref={calculatorRef}
                gestationWeeks={gestationWeeks}
                setGestationWeeks={setGestationWeeks}
                setRecommendedCheckDate={setRecommendedCheckDate}
                setRecommendedSupportWorkshopDate={setRecommendedSupportWorkshopDate}
                setFixedAgeInWeeks={setFixedAgeInWeeks}
                setRecommendedAbrStartDate={setrecommendedAbrStartDate}
                setRecommendedAbrEndDate={setrecommendedAbrEndDate}
                setRecommendedHearingTest1StartDate={setRecommendedHearingTest1StartDate}
                setRecommendedHearingTest1EndDate={setRecommendedHearingTest1EndDate}
                setRecommendedHearingTest2Date={setRecommendedHearingTest2Date}
                setRecommendedHearingTest3Date={setRecommendedHearingTest3Date}
                birthDate={birthDate}
                setBirthDate={setBirthDate}
                onCalculate={handleCalculation}
                onInputChange={handleInputChange}
                currentDate={getCurrentDate()}
                onReset={handleReset}
              />
        </div>

        {isCalculated && (
          <div className={`graphs-container ${isDirty ? 'blurred' : ''}`}>
            <GrowthCharts
              gender={gender}
              gestationWeeks={gestationWeeks}
              fixedAgeInWeeks={fixedAgeInWeeks}
              fentonLength={fentonLength}
              fentonHC={fentonHC}
              fentonWeight={fentonWeight}
              setGender={setGender}
              setFentonLength={setFentonLength}
              setFentonHC={setFentonHC}
              setFentonWeight={setFentonWeight}
              birthDate={birthDate}
            />

            {isPremature && (
              <div
                style={{
                  marginTop: '20px',
                  marginBottom: '15px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  overflow: 'visible',
                }}
              >
              <h2 data-testid="recommendations-title">המלצות</h2>

                <Card
                  ref={growthCardRef}
                  radius="sm"
                  className="w-full"
                  style={{
                    borderRadius: 'var(--radius-m, 8px)',
                    boxShadow:
                      '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)',
                  }}
                  >
                  <CardBody className="space-y-4" style={{ overflow: 'hidden' }}>
                    <button
                      data-testid="toggle-growth-section"
                      onClick={() => {
                        setShowGrowth(!showGrowth);
                        if (!showGrowth) {
                          scrollToCard(growthCardRef);
                        }
                      }}
                      style={{
                        padding: '10px 20px',
                        fontWeight: 'bold',
                        color: '#163D6D',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1.1rem',
                        textAlign: 'right',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        justifyContent: 'flex-end',
                        background: 'transparent',
                        width: '100%',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <div style={{ flex: 1, textAlign: 'right' }}>
                          <h3 style={{ margin: 0 }} >מעקב התפתחות</h3>
                        </div>
                        <span style={{ display: 'flex', alignItems: 'center' }}>
                          {showGrowth ? <ArrowDown /> : <ArrowLeft />}
                        </span>
                      </div>
                    </button>

                    {showGrowth && (
                      <div >
                        <Appointments
                          recommendedCheckDate={recommendedCheckDate}
                          recommendedSupportWorkshopDate={recommendedSupportWorkshopDate}
                        />
                      </div>
                    )}
                  </CardBody>
                </Card>

                <Card
                  ref={hearingCardRef}
                  radius="sm"
                  className="w-full"
                  style={{
                    borderRadius: 'var(--radius-m, 8px)',
                    boxShadow:
                      '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)',
                  }}
                >
                  <CardBody className="space-y-4" style={{ overflow: 'hidden' }}>
                    <button
                      onClick={() => {
                        setShowHearing(!showHearing);
                        if (!showHearing) {
                          scrollToCard(hearingCardRef);
                        }
                      }}
                      style={{
                        padding: '10px 20px',
                        fontWeight: 'bold',
                        color: '#163D6D',
                        border: 'none',
                        cursor: 'pointer',
                        fontSize: '1.1rem',
                        textAlign: 'right',
                        width: '100%',
                        background: 'transparent',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <div style={{ flex: 1, textAlign: 'right' }}>
                          <h3 style={{ margin: 0 }}>מעקב קלינאות תקשורת</h3>
                        </div>
                        <span style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto' }}>
                          {showHearing ? <ArrowDown /> : <ArrowLeft />}
                        </span>
                      </div>
                    </button>

                    {showHearing && (
                      <div>
                        {isPremature ? (
                          <>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '40px',
                                marginBottom: '10px',
                              }}
                            >
                              <h4 style={{ margin: '0 20px 0 0', lineHeight: '1' }}>בדיקות שמיעה</h4>
                              <p style={{ margin: 0, lineHeight: '1' }}>
                                *בהנחה שתוצאות המעקבים תקינות
                              </p>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                              <Monitoring
                                recommendedAbrStartDate={recommendedAbrStartDate}
                                recommendedAbrEndDate={recommendedAbrEndDate}
                                recommendedHearingTest1StartDate={recommendedHearingTest1StartDate}
                                recommendedHearingTest1EndDate={recommendedHearingTest1EndDate}
                                recommendedHearingTest2Date={recommendedHearingTest2Date}
                                recommendedHearingTest3Date={recommendedHearingTest3Date}
                              />
                            </div>
                          </>
                        ) : (
                          <>
                            <h4 style={{ margin: '0 20px 0 0', lineHeight: '1' }}>בדיקות שמיעה</h4>
                            <p style={{ margin: '0 20px 0 0', display: 'block', textAlign: 'right' }}>
                              חלק זה של מעקב שמיעה רלוונטי רק לפגים
                            </p>
                          </>
                        )}
                      </div>
                    )}
                  </CardBody>
                </Card>
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CorrectedAge;